import {useState, useEffect} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  TextField,
  Autocomplete
} from "@mui/material";
import {toast} from "react-toastify";
import {GraphRepository} from "../../../../repositories/GraphRepository";

export const CreateRelationModal = ({ open, onClose }) => {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(false);

  // Subject (from) node state
  const [subjectNodeLabel, setSubjectNodeLabel] = useState("");
  const [subjectNodeId, setSubjectNodeId] = useState("");
  const [subjectNodes, setSubjectNodes] = useState([]);
  const [subjectNodesLoading, setSubjectNodesLoading] = useState(false);

  // Predicate (relationship type) state
  const [relationshipType, setRelationshipType] = useState("");

  // Object (to) node state
  const [objectNodeLabel, setObjectNodeLabel] = useState("");
  const [objectNodeId, setObjectNodeId] = useState("");
  const [objectNodes, setObjectNodes] = useState([]);
  const [objectNodesLoading, setObjectNodesLoading] = useState(false);

  // Fetch schema when modal opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      GraphRepository.getGraphSchema()
        .then((response) => {
          setSchema(response.data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching schema:", error);
          setLoading(false);
        });
    } else {
      // Reset state when modal closes
      setSchema(null);
      setSubjectNodeLabel("");
      setSubjectNodeId("");
      setSubjectNodes([]);
      setRelationshipType("");
      setObjectNodeLabel("");
      setObjectNodeId("");
      setObjectNodes([]);
    }
  }, [open]);

  // Fetch subject nodes when subject label is selected
  useEffect(() => {
    if (subjectNodeLabel) {
      setSubjectNodesLoading(true);
      setSubjectNodeId("");
      GraphRepository.getNodesForLabel(subjectNodeLabel)
        .then((response) => {
          setSubjectNodes(response.data?.nodes || []);
          setSubjectNodesLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching subject nodes:", error);
          setSubjectNodes([]);
          setSubjectNodesLoading(false);
        });
    } else {
      setSubjectNodes([]);
      setSubjectNodeId("");
    }
  }, [subjectNodeLabel]);

  // Fetch object nodes when object label is selected
  useEffect(() => {
    if (objectNodeLabel) {
      setObjectNodesLoading(true);
      setObjectNodeId("");
      GraphRepository.getNodesForLabel(objectNodeLabel)
        .then((response) => {
          setObjectNodes(response.data?.nodes || []);
          setObjectNodesLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching object nodes:", error);
          setObjectNodes([]);
          setObjectNodesLoading(false);
        });
    } else {
      setObjectNodes([]);
      setObjectNodeId("");
    }
  }, [objectNodeLabel]);

  const handleCreate = async () => {
    if (!subjectNodeLabel || !subjectNodeId || !relationshipType || !objectNodeLabel || !objectNodeId) {
      toast.error("Please fill in all required fields");
      return;
    }

    const toastId = toast.loading("Creating relationship...");

    try {
      await GraphRepository.createRelationship(
        subjectNodeLabel,
        subjectNodeId,
        objectNodeLabel,
        objectNodeId,
        relationshipType
      );

      toast.update(toastId, {
        render: "Relationship created successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000
      });

      onClose();
    } catch (error) {
      toast.update(toastId, {
        render: error?.response?.data?.message || "Failed to create relationship",
        type: "error",
        isLoading: false,
        autoClose: 5000
      });
      console.error("Error creating relationship:", error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Relationship</DialogTitle>
      <DialogContent sx={{minHeight: "40dvh"}}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            {/* Subject Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Subject (From Node)
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Subject Node Label</InputLabel>
                <Select
                  value={subjectNodeLabel}
                  onChange={(e) => setSubjectNodeLabel(e.target.value)}
                  label="Subject Node Label"
                >
                  {schema?.node_labels?.map((label) => (
                    <MenuItem key={label} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {subjectNodesLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : subjectNodes.length > 0 ? (
                <FormControl fullWidth>
                  <InputLabel>Subject Node</InputLabel>
                  <Select
                    value={subjectNodeId}
                    onChange={(e) => setSubjectNodeId(e.target.value)}
                    label="Subject Node"
                  >
                    {subjectNodes.map((node) => (
                      <MenuItem key={node.properties.id} value={node?.properties?.id}>
                        {node?.properties?.name || node?.properties?.title || node?.properties?.id} (ID: {node?.properties?.id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : subjectNodeLabel ? (
                <Typography variant="body2" color="text.secondary">
                  No nodes found for label "{subjectNodeLabel}"
                </Typography>
              ) : null}
            </Box>

            {/* Predicate Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Predicate (Relationship Type)
              </Typography>
              <Autocomplete
                freeSolo
                options={schema?.relationship_types || []}
                value={relationshipType}
                onChange={(event, newValue) => {
                  setRelationshipType(newValue ? newValue.toUpperCase() : "");
                }}
                onInputChange={(event, newInputValue) => {
                  setRelationshipType(newInputValue.toUpperCase());
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Relationship Type"
                    placeholder="Select or type relationship type"
                  />
                )}
              />
            </Box>

            {/* Object Section */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Object (To Node)
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Object Node Label</InputLabel>
                <Select
                  value={objectNodeLabel}
                  onChange={(e) => setObjectNodeLabel(e.target.value)}
                  label="Object Node Label"
                >
                  {schema?.node_labels?.map((label) => (
                    <MenuItem key={label} value={label}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {objectNodesLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : objectNodes.length > 0 ? (
                <FormControl fullWidth>
                  <InputLabel>Object Node</InputLabel>
                  <Select
                    value={objectNodeId}
                    onChange={(e) => setObjectNodeId(e.target.value)}
                    label="Object Node"
                  >
                    {objectNodes.map((node) => (
                      <MenuItem key={node.properties.id} value={node?.properties?.id}>
                        {node?.properties?.name || node?.properties?.title || node?.properties?.id} (ID: {node?.properties?.id})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : objectNodeLabel ? (
                <Typography variant="body2" color="text.secondary">
                  No nodes found for label "{objectNodeLabel}"
                </Typography>
              ) : null}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button
          variant="modal"
          onClick={handleCreate}
          disabled={!subjectNodeLabel || !subjectNodeId || !relationshipType || !objectNodeLabel || !objectNodeId}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};